import { type Prisma } from '@prisma/client';
import { type LoaderFunctionArgs } from '@remix-run/node';
import { useLocation } from '@remix-run/react';
import { useEffect, useState } from 'react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { FaceLayout } from '#speaker-shop/components/FaceLayout.tsx';
import { NavigateToHome } from '#speaker-shop/components/NavigateToHome.tsx';
import { Testimonial } from '#speaker-shop/components/Testimonial.tsx';
import { getSpeakerShopData } from '#speaker-shop/models/speaker-shop-data.server.ts';
import { resolveVoiceAndLangParams } from '#speaker-shop/utils/resolve-voice';

type TestimonialRow = Prisma.TestimonialGetPayload<
    { select: { company: true; name: true; id: true; text: true; rating: true } }
>;

export async function loader({ request, params }: LoaderFunctionArgs) {
    const { voiceSlug } = resolveVoiceAndLangParams(params);
    const speakerShopData = await getSpeakerShopData(request.url, voiceSlug);
    if (!speakerShopData) {
        return typedjson({ testimonials: [] as TestimonialRow[] });
    }
    return typedjson({
        testimonials: speakerShopData.testimonials
    });
}

export default function Testimonials() {
    const { t } = useEditableTranslation('testimonials');
    const { testimonials } = useTypedLoaderData<typeof loader>();
    const location = useLocation();
    const [ highlightedId, setHighlightedId ] = useState<string | null>(null);

    useEffect(() => {
        const hash = location.hash.slice(1);
        if (hash && testimonials.some((t) => t.id === hash)) {
            setHighlightedId(hash);
            const timer = setTimeout(() => setHighlightedId(null), 2000);
            return () => clearTimeout(timer);
        }
    }, [ location.hash, testimonials ]);

    return (
        <FaceLayout faceSize='small'>
            <div className='p-8 lg:p-0 max-w-3xl mx-auto lg:mx-0 lg:max-w-7xl'>
                <NavigateToHome />
                <h1 className='font-display text-4xl sm:text-5xl font-extrabold text-slate-900 text-center md:text-left mb-8'>
                    {t('title')}
                </h1>
                <div className='space-y-8'>
                    {testimonials.map((testimonial) => (
                        <div
                            key={testimonial.id}
                            id={testimonial.id}
                            className={`border rounded-lg p-2 md:p-6 transition-all duration-700 ${
                                highlightedId === testimonial.id
                                    ? 'border-blue-400 bg-blue-50 shadow-lg shadow-blue-200/50 scale-[1.02]'
                                    : 'border-slate-200'
                            }`}
                        >
                            <Testimonial data={testimonial} />
                        </div>
                    ))}
                </div>
            </div>
        </FaceLayout>
    );
}
